import { booleanAttribute, Component, computed, EventEmitter, Input, Optional, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormsModule, NgControl, ReactiveFormsModule } from '@angular/forms';
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle, } from '@ng-bootstrap/ng-bootstrap';
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import { distinctUntilChanged } from 'rxjs';
import { SelectOption, SelectOptionPrimitive } from '@models/ui-models/select-options';
import { LinkComponent } from '@shared/components/commons/link/link.component';
import { ValidationErrorComponent } from '@shared/controls/validation-error/validation-error.component';

@Component({
  selector: 'app-autocomplete',
  standalone: true,
  imports: [
    FormsModule,
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    LinkComponent,
    ValidationErrorComponent,
    ReactiveFormsModule
  ],
  templateUrl: './autocomplete.component.html',
  styleUrl: './autocomplete.component.scss'
})
export class AutocompleteComponent implements ControlValueAccessor {
  @Input() placeholder = '';
  @Input() label = '';
  @Input() link = '';
  @Input() text = '';
  @Input() helperText = '';
  @Input() isCombobox = false;
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input({ transform: booleanAttribute }) valuePrimitive = false;
  @Input({ transform: booleanAttribute }) allowTypeAnyValue = false;
  @Input() customErrors = {};
  @Input({ required: true, alias: 'options' }) set optionsSetter(value: SelectOption[]) {
    this.options = value;
    this.optionsSignal.set(value);
  }

  @Output() select = new EventEmitter<{ value: SelectOption | null, index: number | null }>();

  searchControl = new FormControl<string>('');
  #value: SelectOption | null = null;
  dropdownOpen = false;
  options: SelectOption[] = [];
  optionsSignal = signal<SelectOption[]>([]);

  searchControlValueSignal = signal('');
  filteredOptionsSignal = computed(() => {
    const searchControlValue = this.searchControlValueSignal();
    if (!searchControlValue)
      return this.optionsSignal();

    const searchValue = (searchControlValue ?? '')?.toLowerCase();
    return this.optionsSignal()
      .filter((option) => option.label && option.label.toLowerCase().includes(searchValue)
      );
  });

  constructor(@Optional() protected ngControl: NgControl) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }

    this.searchControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((value) => this.handleSearchControlChanges(value));
  }

  get value(): SelectOption | null {
    return this.#value;
  }

  set value(value: SelectOption | null) {
    if (!value && this.searchControl.value && this.isCombobox) {
      const inputValue = this.searchControl.value;
      this.#value = { label: inputValue, value: inputValue };
      this.onChange(this.valuePrimitive ? this.#value?.value : this.#value);
    } else {
      this.#value = value;
      this.onChange(this.valuePrimitive ? this.#value?.value : this.#value);
    }
  }

  get displayValidationError(): boolean {
    return !!(this.ngControl?.errors && this.searchControl.touched);
  }

  selectOption(selectedOption: SelectOption | null, index: number | null): void {
    this.dropdownOpen = false;
    if (!selectedOption) {
      this.searchControl.patchValue('');
    }
    else if (this.searchControl.value !== selectedOption.value) {
      this.searchControl.patchValue(selectedOption.label);
    }
    this.value = selectedOption;
    this.select.emit({ value: selectedOption, index });
  }

  onChange: any = (): void => {
  };
  onTouched: any = (): void => {
  };

  writeValue(value: SelectOption): void {
    if (this.allowTypeAnyValue) {
      this.value = value;
      this.searchControl.patchValue(this.value as unknown as string ?? null);
      return;
    }

    if (this.valuePrimitive) {
      this.value = this.optionsSignal().find((option) => option.value === value as unknown as SelectOptionPrimitive)!;
    } else {
      this.value = value;
    }

    this.searchControl.patchValue(this.value?.label ?? null);
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
    this.searchControl.registerOnChange(fn);
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    setControlDisabled(this.searchControl, isDisabled)
  }

  onDropdownChange(opened: boolean, any?: any): void {
    this.searchControlValueSignal.set('');
  }

  onBlur(): void {
    setTimeout(() => {
      if (this.allowTypeAnyValue || this.dropdownOpen) {
        return;
      }
      if (this.searchControl.value?.toUpperCase() != this.value?.label.toUpperCase()) {
        this.selectOption(null, null);
      }
    }, 100); // Delay clearing to allow selection event to occur
  }

  handleSearchControlChanges(value: string | null): void {
    this.searchControlValueSignal.set(value ?? '');
    this.value = null;

    if (value) {
      if (this.allowTypeAnyValue) {
        this.value = { label: value, value };
        this.searchControl.patchValue(this.value?.label ?? null);
        this.select.emit({ value: this.value, index: 0 });
        return;
      }

      const index = this.optionsSignal().findIndex((option) => option.label === value);
      const match = this.optionsSignal()[index];
      this.value = match ?? null;

      if (match)
        this.select.emit({ value: match, index });

    }
  }
}
